import { Image, Text, TouchableOpacity, TouchableOpacityProps, View } from 'react-native'
import { Link } from 'expo-router'
import { DateTime } from 'luxon'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { mesaColorNames, typography } from '@/src/common/theme'
import { CardBenefitsComponents as Components } from '.'
import { Routes } from '../navigation/routes'
import { REFERRAL_BONUS_OFFER_PARTNER_NAME, INSTACART_PARTNER_NAME } from '../constants'
import type { AvailableBenefit } from '../hooks/useAvailableBenefits'

// Layout constants
const TILE_SIZE = 300
const NEW_BENEFIT_DAYS_THRESHOLD = 14

// Vertical spacing adjustments
// When not enrolled, we need extra space at the top for the enrollment CTA
const UNENROLLED_VERTICAL_OFFSET = 40
const HERO_IMAGE_BASE_HEIGHT = 110
const CARD_CONTENT_BASE_HEIGHT = 190

// Badge positioning
const BADGE_TOP_OFFSET = -8
const BADGE_RIGHT_OFFSET = -10
const BADGE_BORDER_RADIUS = 10
const BADGE_PADDING_HORIZONTAL = 10
const BADGE_PADDING_VERTICAL = 4

// Logo positioning (overlapping hero image and card content)
const LOGO_TOP_BASE = 74
const LOGO_LEFT = 16
const LOGO_SIZE = 64
const LOGO_BORDER_RADIUS = 16

type UserBenefitsEnrollment = {
  benefitId: string
  isEnrolled: boolean
  enrolledAt?: string | Date | null
}

interface BenefitCardProps extends TouchableOpacityProps {
  availableBenefit: AvailableBenefit & { readonly order: number }
  userBenefitsEnrollments: Array<UserBenefitsEnrollment>
  showChevron: boolean
}

export function BenefitCard({
  availableBenefit,
  userBenefitsEnrollments,
  showChevron = false,
  ...props
}: BenefitCardProps) {
  const { benefit, cms: benefitCMS } = availableBenefit

  const enrolledBenefitById = userBenefitsEnrollments.find((b) => b.benefitId === benefit.id)

  const today = DateTime.now()
  const benefitStartDate = new Date(benefit.startDate)
  const start = DateTime.fromJSDate(benefitStartDate)
  const days = today.diff(start, 'days').toObject().days
  const isNewBenefit = typeof days === 'number' && days <= NEW_BENEFIT_DAYS_THRESHOLD

  const isEnrolled = enrolledBenefitById?.isEnrolled
  const verticalOffset = isEnrolled ? 0 : UNENROLLED_VERTICAL_OFFSET

  return (
    <Link
      key={benefit.id}
      asChild
      href={
        benefit.partnerName === REFERRAL_BONUS_OFFER_PARTNER_NAME
          ? { pathname: Routes.ReferralBonusOffer.route }
          : {
              pathname: Routes.Benefit.route,
              params: { partnerName: benefit.partnerName },
            }
      }
    >
      <TouchableOpacity style={props.style} testID={props.testID} key={benefit.partnerName}>
        <View style={{ height: TILE_SIZE, width: '100%', borderRadius: 8 }}>
          <Image
            source={{ uri: benefitCMS.hero_img }}
            style={{
              height: HERO_IMAGE_BASE_HEIGHT + verticalOffset,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          />

          <View
            style={{
              backgroundColor: 'white',
              borderBottomEndRadius: 8,
              borderBottomStartRadius: 8,
              paddingHorizontal: 16,
              paddingBottom: 16,
              height: CARD_CONTENT_BASE_HEIGHT - verticalOffset,
            }}
          >
            <View style={{ flexDirection: 'row', marginBottom: 4, marginTop: 36 }}>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    typography['Base Bold'],
                    { color: mesaColorNames['Mesa - Brand/Blackish #222222'] },
                  ]}
                  numberOfLines={2}
                >
                  {benefitCMS.title}
                </Text>
              </View>

              {showChevron && (
                <View style={{ justifyContent: 'space-evenly' }}>
                  <ChevronRight color={mesaColorNames['Mesa - Brand/Blackish #222222']} size={24} />
                </View>
              )}
            </View>

            <Text
              style={[
                typography.S,
                {
                  color: mesaColorNames['Mesa - Brand/Blackish #222222'],
                  marginVertical: 8,
                },
              ]}
              numberOfLines={1}
            >
              {benefit.partnerName === 'Big Box Membership Credit' ||
              benefit.partnerName === "Lowe's Home Improvement" ||
              benefit.partnerName === INSTACART_PARTNER_NAME
                ? 'Mesa Card Benefit'
                : 'Mesa Card Exclusive'}
            </Text>

            <Components.RedemptionProgress
              isEnrolled={isEnrolled ?? false}
              creditPerRefresh={benefit.creditPerRefresh ?? null}
              benefitId={benefit.id}
              refreshes={benefit.refreshes ?? null}
              creditRedemption={benefit.creditRedemption ?? null}
            />
          </View>

          <View
            style={{
              position: 'absolute',
              top: LOGO_TOP_BASE + verticalOffset,
              left: LOGO_LEFT,
              height: LOGO_SIZE,
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            {isNewBenefit && (
              <View
                style={{
                  position: 'absolute',
                  top: BADGE_TOP_OFFSET,
                  right: BADGE_RIGHT_OFFSET,
                  zIndex: 1,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  backgroundColor:
                    benefitCMS.theme.badgeBackgroundColor ||
                    mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
                  borderRadius: BADGE_BORDER_RADIUS,
                  paddingHorizontal: BADGE_PADDING_HORIZONTAL,
                  paddingVertical: BADGE_PADDING_VERTICAL,
                }}
              >
                <Text
                  style={[
                    typography['XS Bold'],
                    {
                      color: benefitCMS.theme.color || mesaColorNames['Mesa - Brand/White #FFFFFF'],
                      fontSize: 12,
                    },
                  ]}
                >
                  New
                </Text>
              </View>
            )}
            <Image
              source={{ uri: benefitCMS.preview_img }}
              style={[
                {
                  height: '100%',
                  aspectRatio: 1,
                  borderRadius: LOGO_BORDER_RADIUS,
                  overflow: 'hidden',
                },
                benefitCMS.theme.backgroundColor && {
                  backgroundColor: benefitCMS.theme.backgroundColor,
                },
              ]}
            />
          </View>
        </View>
      </TouchableOpacity>
    </Link>
  )
}
